import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getDNSRecords, addDNSRecords, updateDNSRecord, deleteDNSRecord } from '../api'
import { showSimpleSuccess, showSimpleError } from '@/lib/toast.jsx'

// Hook to fetch DNS records for a domain
export const useDNSRecords = (domain, options = {}) => {
  return useQuery({
    queryKey: ['dnsRecords', domain],
    queryFn: () => getDNSRecords(domain),
    enabled: !!domain, // Only run query if domain is provided
    staleTime: 0, // Always consider data stale to ensure fresh data after updates
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    ...options
  })
}

// Hook to add DNS records for a domain
export const useAddDNSRecords = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ domain, records }) => addDNSRecords(domain, records),
    onSuccess: (data, variables) => {
      // Invalidate and refetch DNS records query
      queryClient.invalidateQueries({
        queryKey: ['dnsRecords', variables.domain]
      })
      
      // Optionally update the cache directly with new data
      queryClient.setQueryData(['dnsRecords', variables.domain], (oldData) => {
        if (oldData?.data?.records) {
          return {
            ...oldData,
            data: {
              ...oldData.data,
              records: [...oldData.data.records, ...data.data.newRecords],
              recordCount: data.data.totalRecords
            }
          }
        }
        return oldData
      })
      
      // Show success toast
      showSimpleSuccess(`${variables.records.length} DNS record(s) added successfully to ${variables.domain}`)
    },
    onError: (error, variables) => {
      console.error('Failed to add DNS records:', error)
      showSimpleError(`Failed to add DNS records to ${variables.domain}: ${error.message}`)
    }
  })
}

// Hook to update a DNS record for a domain
export const useUpdateDNSRecord = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ domain, match, update }) => updateDNSRecord(domain, match, update),
    onSuccess: (data, variables) => {
      console.log('✅ Update DNS record success response:', data);
      
      // Optimistically update the cache with the response data
      queryClient.setQueryData(['dnsRecords', variables.domain], (oldData) => {
        if (!oldData?.data?.records) {
          console.log('⚠️ No old data to update');
          return oldData;
        }
        
        console.log('🔄 Updating cache - Match:', variables.match);
        console.log('🔄 Updating cache - Update:', variables.update);
        console.log('🔄 Current records:', oldData.data.records);
        
        // Find and update the matching record
        const updatedRecords = oldData.data.records.map(record => {
          // Match by type, subdomain, and address
          const recordSubdomain = record.subdomain || '';
          const matchSubdomain = variables.match.subdomain || '';
          const matchesType = record.type === variables.match.type;
          const matchesSubdomain = recordSubdomain === matchSubdomain;
          const matchesAddress = record.address === variables.match.address;
          
          if (matchesType && matchesSubdomain && matchesAddress) {
            console.log('✅ Found matching record, updating TTL from', record.ttl, 'to', variables.update.ttl);
            // Update the record with new values - use the TTL from update object
            const updatedRecord = {
              ...record,
              address: variables.update.address || record.address,
              ttl: variables.update.ttl !== undefined ? variables.update.ttl : record.ttl,
              ...(variables.update.priority !== undefined && { priority: variables.update.priority })
            };
            console.log('✅ Updated record:', updatedRecord);
            return updatedRecord;
          }
          return record;
        });
        
        console.log('🔄 Updated records with new TTL values:', updatedRecords.map(r => ({ type: r.type, address: r.address, ttl: r.ttl })));
        
        return {
          ...oldData,
          data: {
            ...oldData.data,
            records: updatedRecords,
            totalRecords: data.data?.totalRecords || oldData.data.totalRecords
          }
        };
      });
      
      // Delay refetch slightly to allow UI to update first
      // Note: If backend is not persisting TTL, this refetch will overwrite with old value
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['dnsRecords', variables.domain],
          refetchType: 'active'
        });
      }, 500);
      
      // Show success toast
      showSimpleSuccess(`DNS record updated successfully for ${variables.domain}`)
    },
    onError: (error, variables) => {
      console.error('Failed to update DNS record:', error)
      showSimpleError(`Failed to update DNS record for ${variables.domain}: ${error.message}`)
    }
  })
}

// Hook to delete a DNS record for a domain
export const useDeleteDNSRecord = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ domain, match }) => deleteDNSRecord(domain, match),
    onSuccess: (data, variables) => {
      // Invalidate and refetch DNS records query
      queryClient.invalidateQueries({
        queryKey: ['dnsRecords', variables.domain]
      })
      
      // Show success toast
      showSimpleSuccess(`DNS record deleted successfully from ${variables.domain}`)
    },
    onError: (error, variables) => {
      console.error('Failed to delete DNS record:', error)
      showSimpleError(`Failed to delete DNS record from ${variables.domain}: ${error.message}`)
    }
  })
}